import React from 'react';
import { get } from 'lodash';
import { useFormContext, FieldError as IFieldError } from 'react-hook-form';
import { FormFieldFocusSelect as IFormFieldFocusSelect } from '@publishing-engine/domain/types/article';
import { FieldLabel } from 'src/components/Field';
import {
  Box,
  Button as ChakraButton,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from '@chakra-ui/react';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';

interface IFormFieldFocusSelectProps {
  config: IFormFieldFocusSelect;
  isEditingAllowed?: boolean;
}

const FormFieldFocusSelect: React.FC<IFormFieldFocusSelectProps> = ({
  config,
  isEditingAllowed = true
}) => {
  const {
    formState: { errors },
    setValue,
    watch
  } = useFormContext();

  const {
    name,
    caseDataFields: { main: field },
    params: { validations, labels }
  } = config;

  const options = validations.options;

  const value = watch(field) as string;
  const error = get(errors, field) as IFieldError;

  return (
    <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
      <FieldLabel
        isRequired={validations.required}
        label={labels.main}
        subText={labels.subtitle}
        helpText={labels.helpText}
      />
      <Menu matchWidth>
        <MenuButton
          id={name}
          as={ChakraButton}
          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
          textAlign="left"
          justifyContent="space-between"
          fontWeight="normal"
          bg="surfacePrimaryFill"
          border="1px solid"
          borderColor={error ? 'red.500' : 'black'}
          _hover={{ bg: 'surfacePrimaryFill' }}
          _active={{ bg: 'surfacePrimaryFill' }}
          _expanded={{ bg: 'surfacePrimaryFill' }}
          width="100%"
          overflow="hidden"
          isDisabled={!isEditingAllowed}
        >
          <Box
            as="span"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {value
              ? labels.options[value]?.main ?? value
              : labels.placeholder || 'Select an option'}
          </Box>
        </MenuButton>
        <MenuList maxH="320px" overflowY="auto">
          <MenuItem
            onClick={() => setValue(field, '', { shouldValidate: true })}
            py={3}
            color="gray.400"
          >
            {labels.placeholder || 'Select an option'}
          </MenuItem>
          {options.map(option => (
            <MenuItem
              key={option}
              onClick={() => setValue(field, option, { shouldValidate: true })}
              py={3}
              whiteSpace="normal"
            >
              {labels.options[option]?.main ?? option}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <FormFieldErrorMessage config={config} />
    </Flex>
  );
};

export default FormFieldFocusSelect;
